import { type Message as TMessage } from '@littlebird/sdk';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { classList } from '@/utils/cn';
import { isTouchPrimaryViewport } from '@/utils/touch-primary';

type FastScrollBarProps = {
  /** The full in-memory thread; the rendered window may be smaller. */
  messages: TMessage[];
  onJumpToMessage: (messageId: string) => void;
  show: boolean;
  /** Visible range in message-index space. */
  getVisibleRange: () => { start: number; end: number };
  getScrollBounds: () => { atTop: boolean; atBottom: boolean };
  scrollContainerId: string;
};

type UserMessageIndicator = {
  index: number;
  id: string;
  preview: string;
};

const MAX_COLLAPSED_DOTS = 8;

const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '…';
};

export const FastScrollBar = ({
  messages,
  onJumpToMessage,
  show,
  getVisibleRange,
  getScrollBounds,
  scrollContainerId,
}: FastScrollBarProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 0 });
  // Default to "at bottom" since chats open scrolled to the latest message
  const [scrollBounds, setScrollBounds] = useState({
    atTop: false,
    atBottom: true,
  });
  // Stored by ID (not index) to survive list reordering when older messages load
  const [userSelectedId, setUserSelectedId] = useState<string | null>(null);
  const lastRangeRef = useRef({ start: 0, end: 0 });
  const lastBoundsRef = useRef({ atTop: false, atBottom: true });
  const hasBeenShownRef = useRef(false);

  // Identity churns per chunk; key the scan on cheap boundaries instead.
  // Relies on append-only messages: edits keeping length and ends would miss.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const messagesSignature = [
    messages.length,
    messages[0]?.id ?? '',
    messages[messages.length - 1]?.id ?? '',
  ].join(':');
  const userMessageIndicators = useMemo(() => {
    const indicators: UserMessageIndicator[] = [];
    messagesRef.current.forEach((message, index) => {
      if (message.role === 'user') {
        const content = message.content || '';
        const firstLine = content.split('\n')[0] || '';
        indicators.push({
          index,
          id: message.id,
          preview: truncateText(firstLine, 48),
        });
      }
    });
    return indicators;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesSignature]);

  useEffect(() => {
    if (!show) return;

    let rafId: number | null = null;

    const updateScrollState = () => {
      const range = getVisibleRange();
      const bounds = getScrollBounds();

      if (
        range.start !== lastRangeRef.current.start ||
        range.end !== lastRangeRef.current.end
      ) {
        lastRangeRef.current = range;
        setVisibleRange(range);
      }

      if (
        bounds.atTop !== lastBoundsRef.current.atTop ||
        bounds.atBottom !== lastBoundsRef.current.atBottom
      ) {
        lastBoundsRef.current = bounds;
        setScrollBounds(bounds);
      }
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        updateScrollState();
        rafId = null;
      });
    };

    updateScrollState();

    // VList (virtua) exposes a handle, not DOM element - getElementById is required
    const container = document.getElementById(scrollContainerId);
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [
    show,
    scrollContainerId,
    getVisibleRange,
    getScrollBounds,
    messages.length,
  ]);

  useEffect(() => {
    if (userSelectedId === null) return;

    const exists = userMessageIndicators.some(
      (ind) => ind.id === userSelectedId
    );
    if (!exists) {
      setUserSelectedId(null);
    }
  }, [userSelectedId, userMessageIndicators]);

  const currentIndicatorIndex = useMemo(() => {
    if (userMessageIndicators.length === 0) return -1;

    // User selection takes precedence, but only once the item is visible (after scroll completes)
    if (userSelectedId !== null) {
      const selectedIndex = userMessageIndicators.findIndex(
        (ind) => ind.id === userSelectedId
      );
      if (selectedIndex !== -1) {
        const indicator = userMessageIndicators[selectedIndex];
        const isVisible =
          indicator.index >= visibleRange.start &&
          indicator.index <= visibleRange.end;
        if (isVisible) {
          return selectedIndex;
        }
      }
    }

    // Exclusive boundaries: at one end but not both (both = content fits, no scroll needed)
    const atTopExclusive = scrollBounds.atTop && !scrollBounds.atBottom;
    const atBottomExclusive = scrollBounds.atBottom && !scrollBounds.atTop;

    if (atTopExclusive) return 0;
    if (atBottomExclusive) return userMessageIndicators.length - 1;

    const visibleIndicatorIndices: number[] = [];
    for (let i = 0; i < userMessageIndicators.length; i++) {
      const indicatorIndex = userMessageIndicators[i].index;
      if (
        indicatorIndex >= visibleRange.start &&
        indicatorIndex <= visibleRange.end
      ) {
        visibleIndicatorIndices.push(i);
      }
    }

    // First visible indicator for linear top-to-bottom progression
    if (visibleIndicatorIndices.length > 0) {
      return visibleIndicatorIndices[0];
    }

    // Scrolled past all messages → last; all messages below → first
    if (
      userMessageIndicators[userMessageIndicators.length - 1].index <
      visibleRange.start
    ) {
      return userMessageIndicators.length - 1;
    }
    if (userMessageIndicators[0].index > visibleRange.end) {
      return 0;
    }

    for (let i = userMessageIndicators.length - 1; i >= 0; i--) {
      if (userMessageIndicators[i].index < visibleRange.start) {
        return i;
      }
    }

    return -1;
  }, [userMessageIndicators, visibleRange, scrollBounds, userSelectedId]);

  const collapsedDotCount = Math.min(
    userMessageIndicators.length,
    MAX_COLLAPSED_DOTS
  );
  const activeDotIndex = useMemo(() => {
    if (currentIndicatorIndex < 0 || userMessageIndicators.length === 0)
      return -1;
    if (userMessageIndicators.length <= MAX_COLLAPSED_DOTS)
      return currentIndicatorIndex;
    return Math.round(
      (currentIndicatorIndex / (userMessageIndicators.length - 1)) *
        (collapsedDotCount - 1)
    );
  }, [currentIndicatorIndex, userMessageIndicators.length, collapsedDotCount]);

  useEffect(() => {
    if (show) hasBeenShownRef.current = true;
  }, [show]);

  if (userMessageIndicators.length < 2) return null;
  // Touch has no hover preview or drag target; native scrolling replaces it
  if (isTouchPrimaryViewport()) return null;

  // Skip fade-in animation on initial mount
  const isFirstShow = show && !hasBeenShownRef.current;

  return (
    <AnimatePresence mode="wait">
      {show && (
        <motion.div
          initial={isFirstShow ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={classList(
            'absolute right-0 top-8',
            'flex flex-col',
            'max-h-[calc(100%-4rem)] overflow-y-auto',
            'pointer-events-auto',
            'z-50'
          )}
          style={{ scrollbarWidth: 'none' }}
        >
          <AnimatePresence mode="wait">
            {isHovered ? (
              <motion.div
                key="expanded"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.15 }}
                className={classList(
                  'flex flex-col gap-1 p-2',
                  'bg-surface border border-border rounded-lg',
                  'shadow-lg',
                  'mr-4'
                )}
              >
                {userMessageIndicators.map((indicator, i) => (
                  <button
                    key={indicator.id}
                    type="button"
                    onClick={() => {
                      onJumpToMessage(indicator.id);
                      setUserSelectedId(indicator.id);
                      setIsHovered(false);
                    }}
                    className={classList(
                      'text-left px-2 py-1.5 rounded-md',
                      'text-sm text-foreground',
                      'cursor-default',
                      'transition-colors duration-100',
                      'truncate',
                      'max-w-72',
                      i === currentIndicatorIndex
                        ? 'bg-secondary'
                        : 'hover:bg-surface-hover'
                    )}
                  >
                    {indicator.preview || 'Message'}
                  </button>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="collapsed"
                initial={isFirstShow ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.1 }}
                className={classList(
                  'flex flex-col items-end gap-2',
                  'py-2 pr-5'
                )}
              >
                {Array.from({ length: collapsedDotCount }, (_, i) => (
                  <div
                    key={i}
                    className={classList(
                      'h-[2px] rounded-full',
                      'transition-all duration-150',
                      i === activeDotIndex
                        ? 'w-4 bg-foreground'
                        : 'w-2.5 bg-foreground-muted'
                    )}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
